import excelToCampaignApi from 'common/src/modules/excelToCampaign/excelToCampaign.api';
import {ToolExecuteParams, ToolResult} from 'common/src/modules/shared/copilot/copilot.types';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';
import toolError from 'common/src/modules/shared/copilot/utils/toolError';
import {z} from 'zod';
import {ExcelToCampaignAgentToolName} from './excelImport.enums';

const inputSchema = z.object({
  campaignId: z.number().describe('The campaign id to create the campaign map for'),
  versionId: z.number().describe('The version id to create the campaign map for'),
  excelFileId: z.string().describe('The excel file id to create the campaign map from'),
  worksheetIndex: z
    .number()
    .describe('The worksheet index to create the campaign map from. Index starts from 0.'),
  headerRowIndex: z.number().describe('The header row index. Index starts from 0.'),
  headerColumnIndex: z.number().describe('The header column index. Index starts from 0.'),
  headerToSignalKeyMapping: z
    .array(
      z.object({
        headerIndex: z.number().describe('The header index to map the signal key to'),
        headerLabel: z.string().describe('The header label to map the signal key to'),
        signalKey: z.string().describe('The signal key to map the header to').nullable(),
      }),
    )
    .describe('The header to signal key mapping'),
  cellValueOverrides: z
    .array(
      z.object({
        rowIndex: z.number().describe('The row index of the cell to override'),
        columnIndex: z.number().describe('The column index of the cell to override'),
        value: z
          .string()
          .describe(
            'The value to override the cell with. Date values should be in the format YYYY-MM-DD.',
          ),
      }),
    )
    .describe('The cells that need to be overridden and their new values'),
});

function createCampaignFromExcelTool(reloadCampaign: () => Promise<void>) {
  async function execute({
    toolUtils,
    input: {
      campaignId,
      versionId,
      excelFileId,
      worksheetIndex,
      headerToSignalKeyMapping,
      headerRowIndex,
      headerColumnIndex,
      cellValueOverrides,
    },
  }: ToolExecuteParams<typeof inputSchema>): Promise<ToolResult> {
    const excelFile = toolUtils.getFileById(excelFileId);

    const headerIndexToSignalKeyMapping = headerToSignalKeyMapping.reduce(
      (acc, {signalKey, headerIndex}) => {
        acc[headerIndex] = signalKey;

        return acc;
      },
      {} as Record<number, string | null>,
    );

    // for TS
    const typedCellValueOverrides = cellValueOverrides.map(({rowIndex, columnIndex, value}) => ({
      rowIndex,
      columnIndex,
      value,
    }));

    await excelToCampaignApi.createCampaign({
      campaignId,
      versionId,
      excelFileKey: excelFile?.fileKey ?? '',
      worksheetIndex,
      headerIndexToSignalKeyMapping,
      headerRowIndex,
      headerColumnIndex,
      cellValueOverrides: typedCellValueOverrides,
    });

    try {
      await reloadCampaign();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';

      return toolError(`Campaign created but failed to reload: ${errorMessage}`);
    }

    return toolResult('Campaign created successfully');
  }

  return tool({
    name: ExcelToCampaignAgentToolName.CreateCampaignFromExcel,
    description: 'Creates a campaign decision tree from mapped excel data.',
    execute,
    displayName: 'Creating campaign map',
    inputSchema,
  });
}

export default createCampaignFromExcelTool;
